import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.js";
import { useCan } from "../auth/useCan.js";
import { useOrgs } from "../orgs/OrgContext.js";
import type { Permission } from "../types.js";

/**
 * Gate a route subtree behind authentication and (optionally) a permission.
 * The permission check is the combined `useCan` — it passes when the user holds
 * the permission globally (platform admin) OR the selected org's role grants it
 * (org-scoped), mirroring the API's authorize + authorizeOrg split. We wait for
 * orgs to load too, so an org-scoped route doesn't flash 403 before the active
 * org (and thus the org role) is known.
 */
export function ProtectedRoute({ permission }: { permission?: Permission }) {
  const { user, loading } = useAuth();
  const { loading: orgsLoading } = useOrgs();
  const can = useCan();

  if (loading || orgsLoading) return <div className="center muted">Loading…</div>;
  // First-time (unauthenticated) visitors land on the marketing home (root); its
  // CTA leads to /login. Deep links still work — after sign-in the router restores.
  if (!user) return <Navigate to="/" replace />;
  if (permission && !can(permission)) {
    return <div className="center muted">403 — you don’t have access to this area.</div>;
  }
  return <Outlet />;
}
